import styled from "styled-components";
import media from "styled-media-query";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { AddToCalendarButton } from "add-to-calendar-button-react";
import { EventScheme, ProductScheme } from "client-v2";

import { Color, Text, Button } from "@adoptaunabuelo/react-components";
import { Calendar, MapPin, Check } from "lucide-react";
import SectionContainer from "../../../components/container/SectionContainer";
import FooterSocialMedia from "../../../components/section/success/FooterSocialMedia";
import SuccessCrossSell from "../../../components/section/success/SuccessCrossSell";
import { useSelector } from "react-redux";
import { State } from "../../../redux";

const Row = styled.div`
	display: flex;
	flex-direction: row;
`;
const RowReverse = styled.div`
	display: flex;
	flex-direction: row;
	width: -webkit-fill-available;
	width: -moz-available;
	border: 1px solid ${Color.line.soft};
	padding: 18px;
	border-radius: 12px;
	${media.lessThan("small")`
        flex-direction: column;
        gap: 24px;
    `}
`;
const Column = styled.div`
	display: flex;
	flex-direction: column;
`;
const CheckIcon = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	height: 20px;
	width: 20px;
	margin-top: 2px;
`;

const Success = (props: Props) => {
	const isMobile = useSelector((state: State) => state.screen.isMobile);

	const onMapClick = () => {
		const location = props.event ? props.event.location : undefined;
		if (location)
			window.open(
				"https://maps.google.com/?q=" +
					location.latitude +
					"," +
					location.longitude,
				"_blank"
			);
	};

	return props.event && props.product ? (
		<SectionContainer style={{ paddingTop: 0 }}>
			<Text type="h3" weight="semibold" style={{ textAlign: "center" }}>
				¡Nos vemos en el evento!
			</Text>
			<Text type="h5" weight="regular" style={{ textAlign: "center" }}>
				{props.product.title}
			</Text>
			<RowReverse
				style={{
					marginTop: 48,
					justifyContent: "space-between",
					gap: 24,
				}}
			>
				<Column style={{ flex: 1, alignItems: "center" }}>
					<Calendar
						style={{ marginBottom: 8 }}
						color={Color.text.full}
					/>
					<Text
						type="p"
						weight="medium"
						style={{ textAlign: "center" }}
					>
						{props.product.startDate
							? dayjs(props.product.startDate.iso).locale("es").format(
									"D [de] MMMM [de] YYYY"
							  )
							: undefined}
					</Text>
					<Text
						type="p2"
						style={{
							color: Color.text.high,
							marginBottom: 8,
							textAlign: "center",
						}}
					>
						De{" "}
						{props.product.startDate
							? dayjs(props.product.startDate.iso).format("HH:mm")
							: undefined}{" "}
						a{" "}
						{props.product.endDate
							? dayjs(props.product.endDate.iso).format("HH:mm")
							: undefined}
					</Text>
					{props.product.startDate && props.product.endDate && (
						<AddToCalendarButton
							buttonStyle="custom"
							customCss="https://parsefiles-spain.back4app.com/QY5gVZmeai8ug19nHGA9AoFmuPU26k04tGJeV1wT/8ee652f2f4fc3e3f6dfb1b049ad4f857_calendar_button-css.css"
							name={props.product.title}
							description={props.event.description}
							organizer={
								"Adopta Un Abuelo | info@adoptaunabuelo.org"
							}
							startDate={dayjs(
								props.product.startDate.iso
							).format("YYYY-MM-DD")}
							startTime={
								props.product.startDate
									? dayjs(props.product.startDate.iso).format(
											"HH:mm"
									  )
									: undefined
							}
							endDate={dayjs(props.product.endDate.iso).format(
								"YYYY-MM-DD"
							)}
							endTime={
								props.product.endDate
									? dayjs(props.product.endDate.iso).format(
											"HH:mm"
									  )
									: undefined
							}
							timeZone="Europe/Madrid"
							location={props.event.address}
							hideIconButton
							hideBackground
							hideCheckmark
							label={"Añadir a tu calendario"}
							language="es"
							options={[
								"Apple",
								"Google",
								"Outlook.com",
								"Yahoo",
								"Microsoft365",
								"MicrosoftTeams",
							]}
						/>
					)}
				</Column>
				<Column style={{ flex: 1, alignItems: "center" }}>
					<MapPin
						style={{ marginBottom: 8 }}
						color={Color.text.full}
					/>
					<Text
						type="p"
						weight="medium"
						style={{ textAlign: "center" }}
					>
						{props.event.shortAddress}
					</Text>
					<Text
						type="p2"
						style={{ color: Color.text.high, textAlign: "center" }}
					>
						{props.event.address}
					</Text>
					<Button
						style={{ marginTop: 8 }}
						design="call-to-action"
						onClick={onMapClick}
					>
						Cómo llegar
					</Button>
				</Column>
			</RowReverse>
			<Column style={{ marginTop: 48 }}>
				<Text type="h4" weight="semibold" style={{ marginBottom: 16 }}>
					Requisitos para los asistentes
				</Text>
				<Row style={{ marginBottom: 12 }}>
					<CheckIcon>
						<Check
							height={16}
							width={16}
							strokeWidth={3}
							color={Color.text.primary}
						/>
					</CheckIcon>
					<Text type="p" style={{ marginLeft: 12 }}>
						Los menores de 16 años tendrán que ir acompañados por
						padres o tutores.
					</Text>
				</Row>
				<Row style={{ marginBottom: 12 }}>
					<CheckIcon>
						<Check
							height={16}
							width={16}
							strokeWidth={3}
							color={Color.text.primary}
						/>
					</CheckIcon>
					<Text type="p" style={{ marginLeft: 12 }}>
						Si el evento es en residencia se aplicarán las medidas
						de seguridad de la residencia.
					</Text>
				</Row>
				<Row style={{ marginBottom: 12 }}>
					<CheckIcon>
						<Check
							height={16}
							width={16}
							strokeWidth={3}
							color={Color.text.primary}
						/>
					</CheckIcon>
					<Text type="p" style={{ marginLeft: 12 }}>
						Informa a Adopta Un Abuelo sobre cualquier alergia o
						restricción alimentaria.
					</Text>
				</Row>
			</Column>
			{props.product.price > 0 && (
				<Column
					style={{
						backgroundColor: Color.background.secondaryLow,
						borderRadius: 12,
						marginTop: 32,
						padding: 16,
					}}
				>
					<Text type="h5" weight="semibold">
						Política de cancelación
					</Text>
					<Text type="p" style={{ marginTop: 8 }}>
						Cancela hasta 48 horas antes de que empiece la
						experiencia para obtener el reembolso completo.
					</Text>
				</Column>
			)}
			{isMobile ? <SuccessCrossSell /> : null}
			<FooterSocialMedia />
		</SectionContainer>
	) : null;
};
export default Success;
export interface Props {
	event?: EventScheme;
	product?: ProductScheme;
}
